import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

type Props = {
    currentPage: number;
    totalPages: number;
    onPrevHandler: () => void;
    onNextHandler: () => void;
    onPageChange: (page: number) => void;
};

export const PaginationControls = ({
    currentPage,
    totalPages,
    onPrevHandler,
    onNextHandler,
    onPageChange,
}: Props) => {
    const displayPage = currentPage + 1;

    const getVisiblePages = () => {
        const pages: (number | string)[] = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (displayPage <= 3) {
                pages.push(1, 2, 3, '...', totalPages);
            } else if (displayPage >= totalPages - 2) {
                pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', displayPage - 1, displayPage, displayPage + 1, '...', totalPages);
            }
        }

        return pages;
    };

    return (
        <Pagination>
            <PaginationContent>
                {/* First Page Button */}
                <PaginationItem className="cursor-pointer">
                    <button
                        onClick={() => onPageChange(0)}
                        disabled={currentPage === 0}
                        className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                            currentPage === 0 ? "pointer-events-none opacity-50" : ""
                        }`}
                        aria-label="First page"
                        type="button"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </button>
                </PaginationItem>

                <PaginationItem className="cursor-pointer">
                    <PaginationPrevious
                        onClick={onPrevHandler}
                        className={currentPage === 0 ? "pointer-events-none opacity-50" : ""}
                    />
                </PaginationItem>

                {getVisiblePages().map((page, index) => (
                    <PaginationItem key={index} className="cursor-pointer">
                        {page === '...' ? (
                            <PaginationEllipsis />
                        ) : (
                            <PaginationLink
                                onClick={() => onPageChange(Number(page) - 1)}
                                isActive={displayPage === page}
                            >
                                {page}
                            </PaginationLink>
                        )}
                    </PaginationItem>
                ))}

                <PaginationItem className="cursor-pointer">
                    <PaginationNext
                        onClick={onNextHandler}
                        className={currentPage === totalPages - 1 ? "pointer-events-none opacity-50" : ""}
                    />
                </PaginationItem>

                {/* Last Page Button */}
                <PaginationItem className="cursor-pointer">
                    <button
                        onClick={() => onPageChange(totalPages - 1)}
                        disabled={currentPage === totalPages - 1}
                        className={`flex items-center justify-center h-9 w-9 rounded-md border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground ${
                            currentPage === totalPages - 1 ? "pointer-events-none opacity-50" : ""
                        }`}
                        aria-label="Last page"
                        type="button"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </button>
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
};
